import { useState, useRef } from 'react';
import { Camera, FileText, ChevronRight, ChevronDown, ChevronUp, Send, ThumbsUp, Flag, Plus } from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import { generateEnhancedWOBriefing, analyzePhoto, generateDebugChecklist } from '../../utils/openai';
import { calculatePartsBudget } from '../../utils/partsIntelligence';
import { KNOWLEDGE_CATEGORIES, VALID_CATEGORIES } from '../../utils/knowledgeEngine';
import { uploadReceiptPhoto } from '../../utils/storage';
import { colors, Header, Card, Button, SelectField, FeedbackWidget, ConfidenceBadge } from '../shared';

// ─── Inline: Parts Prediction Card ───────────────────────────────────────────

const PartsPredictionCard = ({ parts, budget }) => {
  if (!parts || parts.length === 0) return null;

  const displayParts = parts.slice(0, 8);

  const barColor = (confidence) => {
    if (confidence >= 0.7) return colors.green;
    if (confidence >= 0.4) return colors.yellow;
    return colors.textMuted;
  };

  return (
    <Card style={{ borderLeft: `3px solid ${colors.yellow}` }}>
      <p className="text-xs font-semibold mb-3" style={{ color: colors.yellow }}>Predicted Parts</p>
      <div className="space-y-2">
        {displayParts.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs truncate" style={{ color: colors.textPrimary }}>{p.partName}</p>
            </div>
            {p.avgCost > 0 && (
              <span className="text-xs shrink-0" style={{ color: colors.textMuted }}>${p.avgCost.toFixed(2)}</span>
            )}
            <div className="shrink-0" style={{ width: 60, height: 6, background: colors.inputBg, borderRadius: 3 }}>
              <div style={{
                width: `${Math.round(p.confidence * 100)}%`,
                height: 6,
                background: barColor(p.confidence),
                borderRadius: 3,
              }} />
            </div>
          </div>
        ))}
      </div>
      {budget && budget.highConfidenceCount > 0 && (
        <div className="mt-3 pt-2 flex items-center justify-between" style={{ borderTop: `1px solid ${colors.border}` }}>
          <span className="text-xs" style={{ color: colors.textSecondary }}>
            Est. budget ({budget.highConfidenceCount} likely)
          </span>
          <span className="text-sm font-semibold" style={{ color: colors.yellow }}>
            ${budget.highConfidence.toFixed(2)}
          </span>
        </div>
      )}
    </Card>
  );
};

// ─── Inline: Knowledge Notes Section ─────────────────────────────────────────

const KnowledgeNotesSection = ({ unitId, propertyId }) => {
  const {
    getKnowledgeForUnit, addKnowledgeNote, upvoteKnowledgeNote, flagKnowledgeNote,
  } = useData();

  const [expanded, setExpanded] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  const [newText, setNewText] = useState('');
  const [saving, setSaving] = useState(false);

  const notes = getKnowledgeForUnit(unitId);

  const handleSave = async () => {
    if (!newCategory || !newText.trim()) return;
    setSaving(true);
    try {
      await addKnowledgeNote({
        unitId,
        propertyId,
        category: newCategory,
        text: newText.trim(),
      });
      setNewText('');
      setNewCategory('');
      setShowAddForm(false);
    } catch (err) {
      console.error('Error saving knowledge note:', err);
    } finally {
      setSaving(false);
    }
  };

  const displayNotes = showAll ? notes : notes.slice(0, 5);
  const categoryOptions = VALID_CATEGORIES.map(c => ({
    value: c,
    label: KNOWLEDGE_CATEGORIES[c].label,
  }));

  return (
    <Card>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between"
      >
        <span className="text-xs font-semibold" style={{ color: colors.textSecondary }}>
          Tribal Knowledge ({notes.length})
        </span>
        {expanded ? <ChevronUp size={16} style={{ color: colors.textMuted }} /> : <ChevronDown size={16} style={{ color: colors.textMuted }} />}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          {displayNotes.map(note => (
            <div
              key={note.id}
              className="p-2 rounded-lg"
              style={{
                background: colors.inputBg,
                opacity: note.flagged ? 0.5 : 1,
                borderLeft: note.category === 'safety' ? `3px solid ${colors.red}` : undefined,
              }}
            >
              <div className="flex items-start gap-2">
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full shrink-0"
                  style={{
                    background: note.category === 'safety' ? colors.redDim : colors.yellowDim,
                    color: note.category === 'safety' ? colors.red : colors.yellow,
                  }}
                >
                  {KNOWLEDGE_CATEGORIES[note.category]?.label || note.category}
                </span>
                <p
                  className="text-xs flex-1"
                  style={{
                    color: colors.textPrimary,
                    textDecoration: note.flagged ? 'line-through' : undefined,
                  }}
                >
                  {note.text}
                </p>
              </div>
              <div className="flex items-center gap-3 mt-1.5">
                <button
                  onClick={() => upvoteKnowledgeNote(note.id)}
                  className="flex items-center gap-1 text-[10px]"
                  style={{ color: colors.textMuted }}
                >
                  <ThumbsUp size={10} /> {note.upvotes || 0}
                </button>
                <button
                  onClick={() => flagKnowledgeNote(note.id)}
                  className="text-[10px]"
                  style={{ color: colors.textMuted }}
                >
                  <Flag size={10} />
                </button>
                {note.createdBy && (
                  <span className="text-[10px] ml-auto" style={{ color: colors.textMuted }}>
                    — {note.createdBy}
                  </span>
                )}
              </div>
            </div>
          ))}

          {notes.length > 5 && !showAll && (
            <button
              onClick={() => setShowAll(true)}
              className="text-xs w-full text-center py-1"
              style={{ color: colors.yellow }}
            >
              Show all {notes.length} notes
            </button>
          )}

          {!showAddForm ? (
            <button
              onClick={() => setShowAddForm(true)}
              className="flex items-center gap-1 text-xs py-1"
              style={{ color: colors.yellow }}
            >
              <Plus size={12} /> Add Note
            </button>
          ) : (
            <div className="space-y-2 pt-2" style={{ borderTop: `1px solid ${colors.border}` }}>
              <SelectField
                label="Category"
                value={newCategory}
                onChange={setNewCategory}
                options={categoryOptions}
                placeholder="Select category..."
              />
              <textarea
                value={newText}
                onChange={e => setNewText(e.target.value.slice(0, 1000))}
                placeholder="What should the next tech know?"
                rows={2}
                className="w-full px-3 py-2 rounded-xl text-xs outline-none resize-none"
                style={{ background: colors.inputBg, color: colors.textPrimary, border: `1px solid ${colors.border}` }}
              />
              <div className="flex items-center justify-between">
                <span className="text-[10px]" style={{ color: colors.textMuted }}>{newText.length}/1000</span>
                <div className="flex gap-2">
                  <button onClick={() => setShowAddForm(false)} className="text-xs px-3 py-1.5 rounded-lg" style={{ color: colors.textMuted }}>
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving || !newCategory || !newText.trim()}
                    className="text-xs px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50"
                    style={{ background: colors.yellow, color: colors.black }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ConsultScreen = () => {
  const {
    getOpenWOsForTech, getWOsForUnit, getWOsForProperty, getLaborForWO,
    addConsultSession, getIntelligenceForBriefing, getRepairConfidence,
  } = useData();

  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hey! I'm your Trades Consultant. I can help with technical questions, lookup past work orders, or analyze photos of issues. What can I help with?", isWelcome: true }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedWO, setSelectedWO] = useState(null);
  const [showFollowupPills, setShowFollowupPills] = useState(false);
  const [showAllWOs, setShowAllWOs] = useState(false);
  const [currentIntelligence, setCurrentIntelligence] = useState(null);

  const fileInputRef = useRef(null);
  const messageIdRef = useRef(1);
  const openWOs = getOpenWOsForTech();

  const addMessage = (role, text, metadata = {}) => {
    const id = messageIdRef.current++;
    setMessages(prev => [...prev, { role, text, id, ...metadata }]);
  };

  const handleBriefMe = async (wo) => {
    setLoading(true);
    setSelectedWO(wo);
    addMessage('user', `Brief me on ${wo.id}`);

    try {
      // Get unit history (limited to 20, sorted by date)
      const unitHistory = getWOsForUnit(wo.unitId, 20);

      // Get property-wide history (limited to 50, excludes current unit to avoid duplicates)
      const propertyHistory = getWOsForProperty(wo.propertyId, wo.unitId, 50);

      // Get labor notes for unit history only (most relevant)
      const laborNotes = unitHistory.flatMap(h => getLaborForWO(h.id));

      // Gather intelligence
      const intelligence = getIntelligenceForBriefing(wo);
      setCurrentIntelligence(intelligence);

      console.log(`[Consult] Context for ${wo.id}:`, {
        unit: `${wo.unitId} - ${unitHistory.length} WOs`,
        property: `${wo.propertyId} - ${propertyHistory.length} WOs (other units)`,
        laborNotes: laborNotes.length,
        hasIntelligence: !!intelligence,
      });

      // Use enhanced briefing with intelligence
      const briefing = await generateEnhancedWOBriefing(wo, unitHistory, laborNotes, propertyHistory, intelligence);

      addMessage('assistant', `📋 **Briefing for ${wo.id}**\n\n${briefing}`, {
        promptId: 'generateEnhancedBriefing',
        contextId: wo.id,
        contextType: 'work_order',
        output: briefing,
        intelligence,
      });

      // Log consult session (non-blocking - don't fail if demo user)
      try {
        await addConsultSession({
          woId: wo.id,
          type: 'brief',
        });
      } catch (logError) {
        console.log('Could not log consult session:', logError.message);
      }

      setShowFollowupPills(true);
    } catch (error) {
      console.error('Error generating briefing:', error);
      addMessage('assistant', '❌ Sorry, I had trouble generating that briefing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoAnalysis = async (file) => {
    if (!file) return;

    setLoading(true);
    addMessage('user', '📸 Let me show you what I see');

    try {
      // Upload photo first
      const result = await uploadReceiptPhoto(file, selectedWO?.id || 'consult');
      const context = selectedWO ? selectedWO.description : 'Maintenance issue';

      const analysis = await analyzePhoto(result.url, context);

      addMessage('assistant', `📷 **Photo Analysis**\n\n${analysis}`, {
        promptId: 'analyzePhoto',
        contextId: selectedWO?.id || 'consult',
        contextType: 'work_order',
        output: analysis,
      });

      if (selectedWO) {
        try {
          await addConsultSession({
            woId: selectedWO.id,
            type: 'photo',
          });
        } catch (logError) {
          console.log('Could not log consult session:', logError.message);
        }
      }
    } catch (error) {
      console.error('Error analyzing photo:', error);
      addMessage('assistant', "❌ Sorry, I couldn't analyze that photo. Please try again.");
    } finally {
      setLoading(false);
      setShowFollowupPills(false);
    }
  };

  const handleHistoryDeepDive = async () => {
    if (!selectedWO) return;

    setLoading(true);
    addMessage('user', 'Tell me more about the history');

    try {
      // Use unitId for data filtering (canonical identifier)
      const unitHistory = getWOsForUnit(selectedWO.unitId);
      const techsSeen = [...new Set(unitHistory.map(h => h.tech).filter(Boolean))];

      // Use display string (unit) for UI
      let historyText = `📜 **History Deep-dive for Unit ${selectedWO.unit || selectedWO.unitId}**\n\n`;
      historyText += `**${unitHistory.length} work orders** found for this unit.\n\n`;

      if (techsSeen.length > 0) {
        historyText += `**Techs who worked here:**\n`;
        techsSeen.forEach(tech => {
          const techWOs = unitHistory.filter(h => h.tech === tech);
          historyText += `- **${tech}** (${techWOs.length} visits)\n`;
        });
      }

      historyText += `\n**Recent history:**\n`;
      unitHistory.slice(0, 5).forEach(h => {
        historyText += `- ${h.createdDate}: ${h.description} (${h.tech})\n`;
      });

      addMessage('assistant', historyText);

      try {
        await addConsultSession({
          woId: selectedWO.id,
          type: 'history',
        });
      } catch (logError) {
        console.log('Could not log consult session:', logError.message);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      addMessage('assistant', '❌ Sorry, I had trouble fetching the history.');
    } finally {
      setLoading(false);
      setShowFollowupPills(false);
    }
  };

  const handleDebugSolve = async () => {
    if (!selectedWO) return;

    setLoading(true);
    addMessage('user', 'Help me debug/solve this');

    try {
      // Use unitId for data filtering (canonical identifier)
      const unitHistory = getWOsForUnit(selectedWO.unitId);
      const checklist = await generateDebugChecklist(selectedWO.description, unitHistory);

      addMessage('assistant', `🔧 **Debug Checklist**\n\n${checklist}`, {
        promptId: 'generateDebugChecklist',
        contextId: selectedWO.id,
        contextType: 'work_order',
        output: checklist,
      });

      try {
        await addConsultSession({
          woId: selectedWO.id,
          type: 'debug',
        });
      } catch (logError) {
        console.log('Could not log consult session:', logError.message);
      }
    } catch (error) {
      console.error('Error generating debug checklist:', error);
      addMessage('assistant', '❌ Sorry, I had trouble generating the checklist.');
    } finally {
      setLoading(false);
      setShowFollowupPills(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    addMessage('user', userMessage);
    setLoading(true);

    // Simple keyword-based responses for free-form questions
    // TODO: FUTURE - Implement more sophisticated NLP or direct OpenAI chat
    try {
      let response = '';

      if (userMessage.toLowerCase().includes('help') || userMessage.toLowerCase().includes('what can')) {
        response = `I can help you with:\n\n1. **Brief Me** - Tap a work order below to get a quick briefing with history and recommendations\n2. **Photo Analysis** - Upload a photo and I'll help diagnose the issue\n3. **Debug Checklist** - Get a step-by-step troubleshooting guide\n\nTry tapping one of your open work orders to get started!`;
      } else {
        response = `I understand you're asking about: "${userMessage}"\n\nFor the best results, tap one of your open work orders below and I'll give you a detailed briefing with history and recommendations specific to that unit.`;
      }

      addMessage('assistant', response);
    } catch (error) {
      addMessage('assistant', '❌ Sorry, something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col" style={{ background: colors.black }}>
      <Header title="Trades Consultant" subtitle="AI-powered assistance" />

      {/* Messages */}
      <div className="flex-1 px-4 py-4 overflow-y-auto">
        <div className="space-y-3">
          {messages.map((m, i) => (
            <div key={m.id || i}>
              <div className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className="max-w-[85%] p-4 rounded-2xl"
                  style={{
                    background: m.role === 'user' ? colors.yellow : colors.cardBg,
                    color: m.role === 'user' ? colors.black : colors.textPrimary
                  }}
                >
                  <p className="text-sm leading-relaxed whitespace-pre-line">{m.text}</p>
                  {/* Show feedback widget for AI-generated responses */}
                  {m.role === 'assistant' && m.promptId && !m.isWelcome && (
                    <FeedbackWidget
                      promptId={m.promptId}
                      product="trades-app"
                      contextId={m.contextId}
                      contextType={m.contextType}
                      outputSnapshot={m.output}
                      compact
                    />
                  )}
                </div>
              </div>
              {/* Parts prediction card after assistant briefing messages */}
              {m.role === 'assistant' && m.intelligence?._raw?.parts?.length > 0 && (
                <div className="mt-2 max-w-[85%]">
                  <PartsPredictionCard
                    parts={m.intelligence._raw.parts}
                    budget={calculatePartsBudget(m.intelligence._raw.parts)}
                  />
                </div>
              )}
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="p-4 rounded-2xl" style={{ background: colors.cardBg }}>
                <div className="flex gap-1">
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: colors.yellow, animationDelay: '0ms' }} />
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: colors.yellow, animationDelay: '150ms' }} />
                  <div className="w-2 h-2 rounded-full animate-bounce" style={{ background: colors.yellow, animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Brief Me Pills - show when no WO selected */}
        {!selectedWO && messages.length <= 2 && (
          <div className="mt-4">
            <p className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>⚡ Quick Brief - tap a WO you're at:</p>
            <div className="space-y-2">
              {(showAllWOs ? openWOs : openWOs.slice(0, 5)).map(wo => {
                const confidence = getRepairConfidence(wo)?.confidence;
                return (
                  <button
                    key={wo.id}
                    onClick={() => handleBriefMe(wo)}
                    disabled={loading}
                    className="w-full p-3 rounded-xl text-left flex items-center gap-3 disabled:opacity-50"
                    style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}
                  >
                    <div className="p-2 rounded-lg" style={{ background: colors.yellowDim }}>
                      <FileText size={18} style={{ color: colors.yellow }} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-mono text-sm font-semibold" style={{ color: colors.yellow }}>{wo.id}</p>
                        <ConfidenceBadge score={confidence} size="sm" />
                      </div>
                      <p className="text-xs" style={{ color: colors.textMuted }}>{wo.description}</p>
                    </div>
                    <ChevronRight size={18} style={{ color: colors.textMuted }} />
                  </button>
                );
              })}
              {!showAllWOs && openWOs.length > 5 && (
                <button
                  onClick={() => setShowAllWOs(true)}
                  className="w-full p-3 rounded-xl text-center text-sm font-medium"
                  style={{ color: colors.yellow, border: `1px dashed ${colors.border}` }}
                >
                  View all {openWOs.length} work orders
                </button>
              )}
            </div>
          </div>
        )}

        {/* Followup Pills - show after briefing */}
        {showFollowupPills && (
          <div className="mt-4 space-y-2">
            <p className="text-xs font-medium mb-2" style={{ color: colors.textMuted }}>What would you like to do?</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) handlePhotoAnalysis(file);
              }}
              className="hidden"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              className="w-full p-3 rounded-xl text-left flex items-center gap-3 disabled:opacity-50"
              style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}
            >
              <span className="text-lg">📸</span>
              <span className="text-sm" style={{ color: colors.textPrimary }}>Snap a photo & I'll give you thoughts</span>
            </button>
            <button
              onClick={handleHistoryDeepDive}
              disabled={loading}
              className="w-full p-3 rounded-xl text-left flex items-center gap-3 disabled:opacity-50"
              style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}
            >
              <span className="text-lg">📜</span>
              <span className="text-sm" style={{ color: colors.textPrimary }}>Tell me more about history</span>
            </button>
            <button
              onClick={handleDebugSolve}
              disabled={loading}
              className="w-full p-3 rounded-xl text-left flex items-center gap-3 disabled:opacity-50"
              style={{ background: colors.cardBg, border: `1px solid ${colors.border}` }}
            >
              <span className="text-lg">🔧</span>
              <span className="text-sm" style={{ color: colors.textPrimary }}>Help me debug/solve this</span>
            </button>
          </div>
        )}

        {/* Knowledge Notes — below followup pills when WO is selected */}
        {selectedWO && showFollowupPills && (
          <div className="mt-3">
            <KnowledgeNotesSection unitId={selectedWO.unitId} propertyId={selectedWO.propertyId} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="px-4 pb-20">
        <div className="rounded-2xl p-2 flex items-center gap-2" style={{ background: colors.cardBg }}>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="p-3 rounded-xl"
            style={{ background: colors.yellowDim }}
          >
            <Camera size={20} style={{ color: colors.yellow }} />
          </button>
          <input
            type="text"
            placeholder="Ask a question..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSendMessage()}
            className="flex-1 px-3 py-2 outline-none text-sm bg-transparent"
            style={{ color: colors.textPrimary }}
          />
          <button
            onClick={handleSendMessage}
            disabled={!input.trim() || loading}
            className="px-5 py-2 rounded-xl font-semibold text-sm disabled:opacity-50"
            style={{ background: colors.yellow, color: colors.black }}
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsultScreen;
